import { useState } from "react";
import clsx from "clsx";
import { DateTime } from "luxon";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil } from "lucide-react";
import { api, type ForecastReview as Review, type ReviewDay } from "../lib/api";
import { HowToRead } from "./HowToRead";
import { Section } from "./Section";

/**
 * The Forecast page's review: each day's forecast beside what the checkpoint counted, for
 * the people who decide whether to trust it. The number that matters is whether the error is
 * coming down, so the trend is the first picture; the notes say why a day missed, from the
 * calendar (holidays and the travel days around them), from the data (cancellations, a
 * checkpoint count cut short) and from staff, who know what was on in town.
 */

const card = "rounded-xl border border-line p-4";
const th = "px-2 py-1.5 text-left text-[11px] font-semibold tracking-wide text-ink-3 uppercase";
const td = "px-2 py-1.5 align-top";
const num = (v: number | null | undefined) => (v == null ? "—" : v.toLocaleString());
const signed = (v: number | null) => (v == null ? "—" : `${v > 0 ? "+" : ""}${v}%`);
/** Within 10% is green, within 20% amber, beyond that red: an airport's own sense of "close". */
export const errorTone = (pct: number | null) => (pct == null ? "text-ink-3" : Math.abs(pct) <= 10 ? "text-ok" : Math.abs(pct) <= 20 ? "text-warn" : "text-bad");
const errorFill = (pct: number | null) => (pct == null ? "fill-ink-3/40" : Math.abs(pct) <= 10 ? "fill-ok" : Math.abs(pct) <= 20 ? "fill-warn" : "fill-bad");

/** The opening line: how close the forecast has been, and which way it is going. */
export function reviewSentence(r: Review | null): string {
  if (!r || !r.summary.scored) return "No day in this range has both a kept forecast and a full checkpoint count yet.";
  const s = r.summary;
  let out = `Over ${s.scored} scored ${s.scored === 1 ? "day" : "days"} the forecast was off by ${s.meanAbsErrorPct}% on average, and within 10% on ${s.withinTenPct}% of days.`;
  if (s.last7 != null && s.previous7 != null) {
    const diff = Math.round((s.previous7 - s.last7) * 10) / 10;
    out +=
      diff > 0
        ? ` The last seven days averaged ${s.last7}%, better than the ${s.previous7}% of the seven before.`
        : diff < 0
          ? ` The last seven days averaged ${s.last7}%, worse than the ${s.previous7}% of the seven before.`
          : ` The last seven days averaged ${s.last7}%, the same as the seven before.`;
  }
  return out;
}

export function ForecastReviewTab({ today, tz }: { today: string; tz: string }) {
  const [from, setFrom] = useState(() => DateTime.fromISO(today, { zone: tz }).minus({ days: 29 }).toISODate()!);
  const [to, setTo] = useState(today);
  const q = useQuery({ queryKey: ["forecast-review", from, to], queryFn: () => api.forecastReview(from, to) });
  const r = q.data ?? null;

  const download = () => {
    if (!r) return;
    const cols = ["Date", "MadeAt", "LeadHours", "Forecast", "Actual", "DeltaPct", "Rolling7Pct", "PeakForecast", "PeakForecastAt", "PeakActual", "PeakActualAt", "FlightsScored", "FlightMedianErrorPct", "Notes"];
    const esc = (v: unknown) => (v == null ? "" : `"${String(v).replace(/"/g, '""')}"`);
    const lines = [
      cols.join(","),
      ...r.days.map((d) =>
        [
          d.date,
          d.madeAt,
          d.leadHours,
          d.forecast,
          d.actual,
          d.deltaPct,
          d.rolling7,
          d.peak.forecast?.passengers,
          d.peak.forecast?.at,
          d.peak.actual?.passengers,
          d.peak.actual?.at,
          d.flights.scored,
          d.flights.medianErrorPct,
          d.notes.map((n) => n.text).join("; "),
        ]
          .map(esc)
          .join(","),
      ),
    ];
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `forecast-review-${from}-to-${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2 text-[12px]">
        <label className="flex items-center gap-1">
          From <input type="date" className="rounded-md border border-line bg-panel px-2 py-1 text-[12.5px]" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="flex items-center gap-1">
          To <input type="date" className="rounded-md border border-line bg-panel px-2 py-1 text-[12.5px]" value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} />
        </label>
        {q.isFetching && <Loader2 className="size-4 animate-spin text-ink-3" />}
        <button className="ml-auto rounded-md border border-line px-2 py-1 text-ink-2 hover:text-ink disabled:opacity-40" disabled={!r?.days.length} onClick={download}>
          Download CSV
        </button>
      </div>
      <HowToRead id="forecast.review">
        <ReviewNotes />
      </HowToRead>
      {q.isError && <p className="text-[13px] text-bad">{(q.error as Error).message}</p>}
      <p className="mb-4 max-w-3xl text-[14px] text-ink">{reviewSentence(r)}</p>
      {r && r.days.length > 0 && (
        <>
          <div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Card label="Days scored" value={num(r.summary.scored)} sub={`of ${r.days.length} with a kept forecast`} />
            <Card label="Average error" value={r.summary.meanAbsErrorPct == null ? "—" : `${r.summary.meanAbsErrorPct}%`} sub="forecast against the checkpoint, either way" />
            <Card
              label="Last 7 days"
              value={r.summary.last7 == null ? "—" : `${r.summary.last7}%`}
              sub={r.summary.previous7 == null ? "average error" : `was ${r.summary.previous7}% the seven before`}
              tone={r.summary.last7 != null && r.summary.previous7 != null ? (r.summary.last7 < r.summary.previous7 ? "text-ok" : r.summary.last7 > r.summary.previous7 ? "text-bad" : undefined) : undefined}
            />
            <Card label="Within 10%" value={r.summary.withinTenPct == null ? "—" : `${r.summary.withinTenPct}%`} sub="of scored days" />
          </div>

          <Section
            title="Is the forecast getting better?"
            caption="One bar per day: how far the forecast was from what the checkpoint counted, above the line when more were expected than came. Green within 10%, amber within 20%, red beyond. The line is the average error of the seven days up to each day, read on the right; down is better."
          >
            <TrendChart days={r.days} tz={tz} />
          </Section>

          <Section
            title="Each day"
            caption="Newest first. The forecast is the first one kept for the day and says how long before the day it was made. Notes are what was going on: holidays and the travel days around them, cancellations, an incomplete checkpoint count, and anything staff added."
          >
            <DayTable days={[...r.days].reverse()} tz={tz} />
          </Section>
        </>
      )}
    </>
  );
}

export function ReviewNotes() {
  return (
    <>
      <li>The forecast scored for each day is the first one kept for it, made the day before by the hourly job, or whenever someone first opened that day before the job existed.</li>
      <li>The actual is every passenger the security checkpoint counted that day, from the boarding-pass scans (BPSS2). A day is scored once it is over.</li>
      <li>Error is forecast against actual: +10% means 10% more were expected than came.</li>
      <li>Holidays are Canadian, BC and US public holidays, with the travel days around long weekends, US Thanksgiving, the holiday season and BC spring break. Events in town are not known to the app: add them as a note on the day.</li>
      <li>Per flight shows how many departures could be checked one by one, from forecasts kept from late October 2026 on.</li>
    </>
  );
}

function Card({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) {
  return (
    <div className="rounded-xl border border-line p-3">
      <div className="text-[11px] tracking-wide text-ink-3 uppercase">{label}</div>
      <div className={clsx("tabular text-[20px] font-bold", tone)}>{value}</div>
      {sub && <div className="text-[11px] text-ink-3">{sub}</div>}
    </div>
  );
}

/** Signed error bars around a zero line, and the seven-day average error as a line on its own scale. */
export function TrendChart({ days, tz }: { days: ReviewDay[]; tz: string }) {
  const [at, setAt] = useState<string | null>(null);
  const scored = days.filter((d) => d.deltaPct != null);
  if (!scored.length) return <p className="text-[12.5px] text-ink-3">No day in the range is scored yet.</p>;
  const top = Math.max(10, ...scored.map((d) => Math.abs(d.deltaPct!)));
  const lineTop = Math.max(10, ...days.map((d) => d.rolling7 ?? 0));
  const w = 100 / days.length;
  const y = (pct: number) => 50 - (pct / top) * 48;
  const line = days.flatMap((d, i) => (d.rolling7 == null ? [] : [`${i * w + w / 2},${100 - (d.rolling7 / lineTop) * 96}`]));
  const hovered = days.find((d) => d.date === at) ?? null;
  return (
    <div className={clsx(card, "mb-4")} onMouseLeave={() => setAt(null)}>
      <div className="mb-2 flex justify-between text-[11.5px] text-ink-3">
        <span>{`+${Math.round(top)}% more expected than came`}</span>
        <span>{hovered ? `${DateTime.fromISO(hovered.date, { zone: tz }).toFormat("ccc d LLL")}: ${signed(hovered.deltaPct)}${hovered.rolling7 != null ? ` · 7-day ${hovered.rolling7}%` : ""}` : "Hover a day"}</span>
      </div>
      <div className="relative h-44">
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
          <line x1={0} x2={100} y1={50} y2={50} className="stroke-line" strokeWidth={1} vectorEffect="non-scaling-stroke" />
          {days.map((d, i) =>
            d.deltaPct == null ? null : (
              <rect key={d.date} x={i * w + w * 0.15} width={w * 0.7} y={Math.min(50, y(d.deltaPct))} height={Math.abs(y(d.deltaPct) - 50)} className={clsx(errorFill(d.deltaPct), at === d.date ? "opacity-100" : "opacity-80")} />
            ),
          )}
          {line.length > 1 && <polyline points={line.join(" ")} fill="none" className="stroke-ink" strokeWidth={2} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />}
        </svg>
        <div className="absolute inset-0 flex">
          {days.map((d) => (
            <button key={d.date} className="h-full flex-1 cursor-default" onMouseEnter={() => setAt(d.date)} onFocus={() => setAt(d.date)} aria-label={`${d.date}: ${signed(d.deltaPct)}`} />
          ))}
        </div>
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-ink-3">
        <span>{DateTime.fromISO(days[0]!.date, { zone: tz }).toFormat("d LLL")}</span>
        <span>{`${Math.round(top)}% fewer expected than came`}</span>
        <span>{DateTime.fromISO(days.at(-1)!.date, { zone: tz }).toFormat("d LLL")}</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-ink-3">
        <span className="flex items-center gap-1">
          <span className="inline-block size-2.5 rounded-sm bg-ok" /> within 10%
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block size-2.5 rounded-sm bg-warn" /> within 20%
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block size-2.5 rounded-sm bg-bad" /> beyond
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-0.5 w-4 rounded bg-ink" /> 7-day average error
        </span>
      </div>
    </div>
  );
}

const NOTE_TONE: Record<string, string> = { holiday: "bg-info/15 text-info", travel: "bg-info/10 text-info", cancelled: "bg-bad/10 text-bad", data: "bg-warn/15 text-warn", staff: "bg-panel-2 text-ink" };

function DayTable({ days, tz }: { days: ReviewDay[]; tz: string }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [text, setText] = useState("");
  const save = useMutation({
    mutationFn: ({ date, note }: { date: string; note: string }) => api.setForecastNote(date, note),
    onSuccess: () => {
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ["forecast-review"] });
    },
  });
  const hhmm = (iso: string | undefined) => (iso ? DateTime.fromISO(iso, { zone: tz }).toFormat("HH:mm") : "");
  return (
    <div className="scroll-thin overflow-x-auto">
      <table className="w-full min-w-[860px] text-[12.5px]">
        <thead>
          <tr className="border-b border-line">
            <th className={th}>Day</th>
            <th className={th} title="Passengers expected through security, and how long before the day the forecast was made">
              Forecast
            </th>
            <th className={th} title="Passengers the checkpoint counted">
              Actual
            </th>
            <th className={th}>Error</th>
            <th className={th} title="The busiest quarter hour: expected, then counted">
              Busiest quarter hour
            </th>
            <th className={th} title="Departures whose own forecast could be checked, and their typical error">
              Per flight
            </th>
            <th className={th}>Notes</th>
          </tr>
        </thead>
        <tbody>
          {days.map((d) => {
            const staff = d.notes.find((n) => n.kind === "staff");
            return (
              <tr key={d.date} className="tabular border-b border-line">
                <td className={clsx(td, "font-semibold")}>{DateTime.fromISO(d.date, { zone: tz }).toFormat("ccc d LLL")}</td>
                <td className={td}>
                  {num(d.forecast)}
                  <div className="text-[10.5px] text-ink-3">{d.leadHours > 0 ? `${d.leadHours} h before` : "made on the day"}</div>
                </td>
                <td className={td}>{d.actual == null ? <span className="text-ink-3">not over</span> : num(d.actual)}</td>
                <td className={clsx(td, "font-semibold", errorTone(d.deltaPct))}>{signed(d.deltaPct)}</td>
                <td className={td}>
                  {d.peak.forecast ? `${d.peak.forecast.passengers} at ${hhmm(d.peak.forecast.at)}` : "—"}
                  <div className="text-[10.5px] text-ink-3">{d.peak.actual ? `counted ${d.peak.actual.passengers} at ${hhmm(d.peak.actual.at)}` : ""}</div>
                </td>
                <td className={td}>
                  {d.flights.scored ? `${d.flights.medianErrorPct}% typical` : "—"}
                  <div className="text-[10.5px] text-ink-3">{d.flights.scored ? `${d.flights.scored} departures` : ""}</div>
                </td>
                <td className={clsx(td, "max-w-[22rem]")}>
                  <div className="flex flex-wrap gap-1">
                    {d.notes
                      .filter((n) => n.kind !== "staff")
                      .map((n, i) => (
                        <span key={i} className={clsx("rounded px-1.5 py-0.5 text-[11px]", NOTE_TONE[n.kind])}>
                          {n.text}
                        </span>
                      ))}
                  </div>
                  {editing === d.date ? (
                    <form
                      className="mt-1 flex gap-1"
                      onSubmit={(e) => {
                        e.preventDefault();
                        save.mutate({ date: d.date, note: text });
                      }}
                    >
                      <input autoFocus className="flex-1 rounded border border-line bg-panel px-1.5 py-0.5 text-[12px]" maxLength={300} value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. hockey final in town; snow closed YVR" />
                      <button className="rounded border border-line px-1.5 text-[11.5px]">Save</button>
                    </form>
                  ) : (
                    <button
                      className="mt-1 flex items-center gap-1 text-left text-[11.5px] text-ink-2 hover:text-ink"
                      onClick={() => {
                        setEditing(d.date);
                        setText(staff?.text ?? "");
                      }}
                    >
                      <Pencil className="size-3 shrink-0" />
                      {staff ? `${staff.text}${staff.by ? ` (${staff.by})` : ""}` : "Add a note"}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
